import React, { useEffect } from 'react';

import mapboxgl from 'mapbox-gl';

import { MarkerManager, MarkerData, LiveLineData } from './mapTypes';

export function useStaticMarkers(
  map: React.RefObject<mapboxgl.Map | null>,
  markerManagerRef: React.RefObject<MarkerManager>,
  markers: { [id: string]: MarkerData } | undefined,
  liveLine: LiveLineData | undefined,
  mapLoaded: boolean
) {
  useEffect(() => {
    if (!map.current || !mapLoaded || !markerManagerRef.current) {
      return;
    }

    const manager = markerManagerRef.current;
    const liveLineMarkerId = liveLine?.deviceId;
    const liveLineGimbalMarkerId = liveLine?.deviceId ? `${liveLine.deviceId}_gimbal` : null;

    const isLiveLineMarker = (markerId: string): boolean => {
      return markerId === liveLineMarkerId || markerId === liveLineGimbalMarkerId;
    };

    if (markers) {
      Object.values(markers).forEach((markerData) => {
        const existingMarker = manager.getMarker(markerData.id);
        if (existingMarker && existingMarker.getElement().isConnected) {
          manager.updateMarker(markerData.id, markerData.position);
          return;
        }

        manager.createMarker(markerData.id, markerData.image, markerData.position, map.current!);
      });

      Array.from(manager.markers.keys()).forEach((markerId) => {
        const marker = manager.getMarker(markerId);
        if (!marker) return;

        const shouldKeep = markers[markerId] || isLiveLineMarker(markerId);
        if (!shouldKeep && marker.getElement().isConnected) {
          manager.removeMarker(markerId);
        }
      });
    } else {
      Array.from(manager.markers.keys()).forEach((markerId) => {
        const marker = manager.getMarker(markerId);
        if (!marker) return;

        if (!isLiveLineMarker(markerId) && marker.getElement().isConnected) {
          manager.removeMarker(markerId);
        }
      });
    }
  }, [markers, liveLine?.deviceId, mapLoaded, map, markerManagerRef]);
}
